import { useState } from 'react';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';

import { useHideReport, useRestoreReport } from '@/features/hideReport';
import { StructuredReportsAndStudiesType } from '@/features/reportCards';

import { useReportCard } from './useReportCard';

type UseReportCardActionsOptions = {
  structuredReportsAndStudies: StructuredReportsAndStudiesType;
  reportCard: ReturnType<typeof useReportCard>;
};

export const useReportCardActions = ({
  structuredReportsAndStudies,
  reportCard,
}: UseReportCardActionsOptions) => {
  const {
    report,
    reportType,
    superimpositionOrientation,
    shouldHideDownload,
    downloadReportHandler,
    removeReportHandler,
  } = reportCard;

  const dispatch = useAppDispatch();
  const [isRemoving, setIsRemoving] = useState(false);

  const hideReport = useHideReport();
  const restoreReport = useRestoreReport();

  const enableArchiveReportsFeature = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_ArchiveReports,
    ),
  );

  const {
    CanArchive = false,
    CanDelete = false,
    CanRestore = false,
  } = report?.YourPermissions ?? {};
  const isReportHidden = Boolean(report?.Archived?.Archived);

  const handleRemoveWithAnimation = async () => {
    const confirmed = await removeReportHandler({
      onError: () => setIsRemoving(false),
    });

    if (confirmed) {
      setIsRemoving(true);
    }
  };

  const handleHideWithAnimation = async () => {
    const confirmed = await hideReport(
      structuredReportsAndStudies.activeReportIDs,
      { onError: () => setIsRemoving(false) },
    );

    if (confirmed) {
      setIsRemoving(true);
    }
  };

  const handleRestoreWithAnimation = async () => {
    setIsRemoving(true);

    const restored = await restoreReport(
      structuredReportsAndStudies.activeReportIDs,
    );

    if (!restored) {
      setIsRemoving(false);
    }
  };

  // A hidden report can only be deleted for good, and only by its owner; an
  // active one is hidden while the archive feature is on, deleted otherwise.
  const getMobileRemoveReportHandler = () => {
    if (isReportHidden) {
      return CanDelete ? handleRemoveWithAnimation : undefined;
    }

    return enableArchiveReportsFeature
      ? handleHideWithAnimation
      : handleRemoveWithAnimation;
  };

  const openReportInfoModal = () => {
    if (!report) {
      return;
    }

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.ReportInfo,
        data: {
          sourceStudyIDs: report.SourceStudyIDs,
          reportType,
          orientation: superimpositionOrientation,
          shouldHideDownload,
          hideMode: enableArchiveReportsFeature,
          isHidden: isReportHidden,
          removeReport: getMobileRemoveReportHandler(),
          restoreReport:
            isReportHidden && CanRestore
              ? handleRestoreWithAnimation
              : undefined,
          downloadReport: downloadReportHandler,
        },
      }),
    );
  };

  const hasMobileActions = !(isReportHidden && !CanRestore && !CanDelete);

  return {
    isRemoving,
    isReportHidden,
    enableArchiveReportsFeature,
    CanArchive,
    CanDelete,
    CanRestore,
    hasMobileActions,
    handleRemoveWithAnimation,
    handleHideWithAnimation,
    handleRestoreWithAnimation,
    openReportInfoModal,
  };
};
